import { TOUR_STORAGE_KEY_PREFIX } from '../constants';

const COMPLETED_VALUE = 'true';

/**
 * Builds the storage key that records tour completion for a given user.
 * @param userId - Stable user identifier (OIDC `sub`).
 * @returns The per-user storage key.
 */
export function buildTourStorageKey(userId: string): string {
  return `${TOUR_STORAGE_KEY_PREFIX}:${userId}`;
}

/**
 * Resolves `localStorage`, which can throw when site data is blocked.
 * @returns The storage, or `null` when it is unavailable.
 */
export function getDefaultStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/**
 * Checks whether the user has already completed or skipped the first-access tour.
 * Unavailable storage counts as completed: completion cannot be persisted, so the tour would reopen on every load.
 * @param storage - Storage backend (usually `localStorage`), or `null` when unavailable.
 * @param userId - Stable user identifier (OIDC `sub`).
 * @returns `true` when the tour was already completed or storage cannot be read.
 */
export function hasCompletedTour(storage: Storage | null, userId: string): boolean {
  if (!storage) return true;
  try {
    return storage.getItem(buildTourStorageKey(userId)) === COMPLETED_VALUE;
  } catch {
    return true;
  }
}

/**
 * Persists that the user has completed or skipped the first-access tour; write failures are ignored.
 * @param storage - Storage backend (usually `localStorage`), or `null` when unavailable.
 * @param userId - Stable user identifier (OIDC `sub`).
 */
export function markTourCompleted(storage: Storage | null, userId: string): void {
  if (!storage) return;
  try {
    storage.setItem(buildTourStorageKey(userId), COMPLETED_VALUE);
  } catch {
    // Storage blocked or full: the tour simply may reopen next time.
  }
}
